import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  ALL_CEREMONY_TEMPLATES_QUERY_KEY,
  CEREMONY_TEMPLATE_API_URL,
  CEREMONY_TEMPLATE_BY_ID_QUERY_KEY,
  CREATE_CEREMONY_TEMPLATE
} from '@/constants';
import { apiUrl, getQueryParams } from '@/helpers';
import { CreateEditCeremonyTemplatePayload, ErrorResponse, Options } from '@/types';

export const ceremonyTemplateEndpoints = {
  getAll: (pageSize: number, cursor: number, organizationId?: number) =>
    axios.get(
      `${apiUrl()}${CEREMONY_TEMPLATE_API_URL}${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'filter.OrganizationId': organizationId as number
      })}`
    ),
  createCeremonyTemplate: (template: CreateEditCeremonyTemplatePayload) =>
    axios.post(`${apiUrl()}${CEREMONY_TEMPLATE_API_URL}`, template),
  getCeremonyTemplateById: (id: number) =>
    axios.get(`${apiUrl()}${CEREMONY_TEMPLATE_API_URL}/${id}`),
  deleteCeremonyTemplate: (id: number) =>
    axios.delete(`${apiUrl()}${CEREMONY_TEMPLATE_API_URL}/${id}`),
  updateCeremonyTemplate: (template: CreateEditCeremonyTemplatePayload) =>
    axios.put(`${apiUrl()}${CEREMONY_TEMPLATE_API_URL}`, template)
};

export const useGetAllCeremonyTemplates = (
  pageSize: number,
  organizationId?: number,
  enabled?: boolean
) => {
  return useInfiniteQuery(
    [ALL_CEREMONY_TEMPLATES_QUERY_KEY, { pageSize, organizationId }],
    ({ pageParam }) => ceremonyTemplateEndpoints.getAll(pageSize, pageParam, organizationId),
    {
      keepPreviousData: true,
      enabled: enabled,
      refetchOnMount: 'always',
      cacheTime: 0,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage && firstPage.data?.previousCursor !== undefined) {
          return firstPage.data?.previousCursor;
        }
        return undefined;
      }
    }
  );
};

export const useCreateCeremonyTemplateMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(ceremonyTemplateEndpoints.createCeremonyTemplate, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([CREATE_CEREMONY_TEMPLATE]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useGetCeremonyTemplateByIdQuery = (id: number, options?: Options) => {
  return useQuery(
    [CEREMONY_TEMPLATE_BY_ID_QUERY_KEY, { id }],
    () => ceremonyTemplateEndpoints.getCeremonyTemplateById(id),
    {
      enabled: !!id,
      retry: false,
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useDeleteCeremonyTemplateMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(ceremonyTemplateEndpoints.deleteCeremonyTemplate, {
    onSuccess: () => {
      options?.onSuccess();
      queryClient.invalidateQueries([ALL_CEREMONY_TEMPLATES_QUERY_KEY]);
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateCeremonyTemplateMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(ceremonyTemplateEndpoints.updateCeremonyTemplate, {
    onSuccess: () => {
      options?.onSuccess();
      queryClient.invalidateQueries([ALL_CEREMONY_TEMPLATES_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
